import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Save, Server, ShieldCheck, Mail, Building2, CreditCard, Globe2, AlertTriangle, Lock, Send, Link2, Wand2, RefreshCcw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { useAdminPermissions } from "@/admin/PermissionsContext";
import { api, formatApiError } from "@/api/client";
import { Heading } from "@/design-system/components/Heading";
import { Section } from "@/admin/_primitives";

/**
 * SystemConfig — `/admin/system-config`. Wave 5-Early ANCHOR.
 *
 * Consumes `Card` and `Heading` design-system primitives. Every hex
 * literal (38 baseline) and legacy V2 CSS-var ref (72 baseline) burned
 * down to `--ds-*` tokens. Inputs on `--ds-radius-field` (4px), Sections
 * `--ds-radius-card` (4px), micro-labels at 11px uppercase tracking
 * ink-subtle, section titles via Heading level=3 size="md" semibold.
 *
 * All testids, API calls, permission gating, and field-level business
 * logic preserved BYTE-EXACT.
 */


function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span
        className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </span>
      {children}
      {hint && (
        <p
          className="text-[11px] mt-1 leading-snug"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          {hint}
        </p>
      )}
    </label>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className="w-full rounded-[var(--ds-radius-input)] border bg-[var(--ds-color-surface)] px-2.5 py-1.5 text-[13px] text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-input-border-strong)] disabled:opacity-50"
      style={{ borderColor: "var(--ds-color-input-border)", ...(props.style || {}) }}
    />
  );
}

function PostmarkWebhookRow({ cfg, reload, secretInput, setSecretInput }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function fetchUrl() {
    try {
      const { data } = await api.get("/admin/system-config/postmark-webhook-url");
      setUrl(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  useEffect(() => { fetchUrl(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function rotate() {
    if (!confirm("Rotate the Postmark webhook secret? You'll need to update the URL in the Postmark dashboard after rotating, or webhook deliveries will fail.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/system-config/rotate-postmark-webhook-secret");
      toast.success("Webhook secret rotated. Update Postmark with the new URL.");
      setUrl({ configured: true, url: data.url });
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function copyUrl() {
    if (!url?.url) return;
    try {
      await navigator.clipboard.writeText(url.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Could not copy. Select the URL manually."); }
  }

  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1">Webhook URL <span className="ml-2 text-[10px] normal-case tracking-normal" style={{ color: cfg.postmark_webhook_secret_set ? "var(--ds-color-success)" : "var(--ds-color-warning)" }}>{cfg.postmark_webhook_secret_set ? "secret configured" : "no secret"}</span></label>
      {url?.configured ? (
        <div className="flex gap-2">
          <input data-testid="cfg-postmark-webhook-url" readOnly value={url.url} className="flex-1 rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] bg-transparent px-3 py-2 text-xs text-[var(--ds-color-ink-muted)] font-mono" />
          <button data-testid="cfg-postmark-copy-url" onClick={copyUrl} className="rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] px-3 py-2 text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]">{copied ? "Copied!" : "Copy"}</button>
        </div>
      ) : (
        <p className="text-xs text-[var(--ds-color-ink-muted)] mb-2">No webhook secret configured. Generate one to enable signed Postmark webhooks.</p>
      )}
      <button data-testid="cfg-postmark-rotate" onClick={rotate} disabled={busy} className="mt-2 inline-flex items-center gap-1.5 rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] px-3 py-1.5 text-[11px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] disabled:opacity-50">
        {busy ? "Rotating…" : cfg.postmark_webhook_secret_set ? "Rotate webhook secret" : "Generate webhook secret"}
      </button>
      <p className="text-[11px] text-[var(--ds-color-ink-muted)] mt-2 leading-relaxed">Paste this URL into Postmark → Server → Webhooks. Rotating invalidates the previous URL.</p>
    </div>
  );
}


function LobWebhookRow({ cfg, reload }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function fetchUrl() {
    try {
      const { data } = await api.get("/admin/system-config/lob-webhook-url");
      setUrl(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  useEffect(() => { fetchUrl(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function rotate() {
    if (!confirm("Rotate the Lob webhook secret? You'll need to update the URL in the Lob dashboard after rotating, or webhook deliveries will fail.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/system-config/rotate-lob-webhook-secret");
      toast.success("Lob webhook secret rotated. Update Lob with the new URL.");
      setUrl({ configured: true, url: data.url });
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function copyUrl() {
    if (!url?.url) return;
    try {
      await navigator.clipboard.writeText(url.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Could not copy. Select the URL manually."); }
  }

  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1">Webhook URL <span className="ml-2 text-[10px] normal-case tracking-normal" style={{ color: cfg.lob_webhook_secret_set ? "var(--ds-color-success)" : "var(--ds-color-warning)" }}>{cfg.lob_webhook_secret_set ? "secret configured" : "no secret"}</span></label>
      {url?.configured ? (
        <div className="flex gap-2">
          <input data-testid="cfg-lob-webhook-url" readOnly value={url.url} className="flex-1 rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] bg-transparent px-3 py-2 text-xs text-[var(--ds-color-ink-muted)] font-mono" />
          <button data-testid="cfg-lob-copy-url" onClick={copyUrl} className="rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] px-3 py-2 text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]">{copied ? "Copied!" : "Copy"}</button>
        </div>
      ) : (
        <p className="text-xs text-[var(--ds-color-ink-muted)] mb-2">No webhook secret configured. Generate one to enable signed Lob webhooks.</p>
      )}
      <button data-testid="cfg-lob-rotate" onClick={rotate} disabled={busy} className="mt-2 inline-flex items-center gap-1.5 rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] px-3 py-1.5 text-[11px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] disabled:opacity-50">
        {busy ? "Rotating…" : cfg.lob_webhook_secret_set ? "Rotate webhook secret" : "Generate webhook secret"}
      </button>
      <p className="text-[11px] text-[var(--ds-color-ink-muted)] mt-2 leading-relaxed">Paste this URL into Lob → Webhooks. Rotating invalidates the previous URL.</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Iteration_12 — Webhook health card.                                 */
/* Sits under the Billing section; hits                                */
/*   GET  /api/admin/diagnostic/webhook-status                         */
/* on mount + on Refresh, surfaces the canonical Stripe endpoint URL   */
/* the human should paste into the Stripe Dashboard, the last-4 of the */
/* stored webhook secret for visual match, plus the "hint" line the    */
/* backend computes from processed / failure counts.                   */
/* ------------------------------------------------------------------ */
function WebhookHealthCard() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get("/admin/diagnostic/webhook-status");
      setState({ loading: false, data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: formatApiError(e) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const d = state.data;
  const url = d?.webhook_endpoint_url || "";
  const secretConfigured = !!d?.webhook_secret_configured;
  const failures = d?.last_signature_failure_count_24h ?? 0;
  const processed = d?.processed_events_count_24h ?? 0;
  const behind = !!d?.checkout_ahead_of_webhook;

  let statusColor = "var(--ds-color-warning)";
  let statusLabel = "Unknown";
  if (!state.loading && d) {
    if (!secretConfigured) { statusColor = "var(--ds-color-danger)"; statusLabel = "No signing secret"; }
    else if (failures > 0 && processed === 0) { statusColor = "var(--ds-color-danger)"; statusLabel = "Signing secret mismatch"; }
    else if (behind) { statusColor = "var(--ds-color-danger)"; statusLabel = "Webhook not landing"; }
    else if (processed === 0 && failures === 0) { statusColor = "var(--ds-color-warning)"; statusLabel = "No traffic — check Dashboard URL"; }
    else { statusColor = "var(--ds-color-success)"; statusLabel = "Healthy"; }
  }

  return (
    <div
      data-testid="webhook-health-card"
      className="rounded-[var(--ds-radius-card)] border border-[var(--ds-color-admin-border)] p-4 mt-2"
      style={{ background: "var(--ds-color-accent-soft)" }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" style={{ color: statusColor }} />
          <div className="font-display text-sm">Webhook health</div>
          <span
            data-testid="webhook-health-status"
            className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded"
            style={{ color: statusColor, background: `${statusColor}18` }}
          >
            {statusLabel}
          </span>
        </div>
        <button
          data-testid="webhook-health-refresh"
          onClick={load}
          disabled={state.loading}
          className="rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] px-2.5 py-1 text-[11px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
        >
          <RefreshCcw className={`w-3 h-3 inline mr-1 ${state.loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {state.error ? (
        <p className="text-[11px]" style={{ color: "var(--ds-color-danger)" }}>{state.error}</p>
      ) : (
        <>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1" htmlFor="webhook-health-url">
            Endpoint URL to paste into Stripe Dashboard
          </label>
          <div className="flex items-center gap-2 mb-2">
            <Input id="webhook-health-url" data-testid="webhook-health-url" readOnly value={url} />
            <button
              data-testid="webhook-health-copy"
              onClick={() => {
                if (!url) return;
                navigator.clipboard.writeText(url).then(() => {
                  setCopied(true);
                  toast.success("Endpoint URL copied");
                  setTimeout(() => setCopied(false), 1500);
                });
              }}
              className="rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] px-3 py-2 text-[11px] whitespace-nowrap text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
            >
              {copied ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : null}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="text-[10px] text-[var(--ds-color-ink-muted)] mb-3">
            The plural {" "}
            <span className="text-[var(--ds-color-warning)]">
              {d?.deprecated_endpoint_url || ""}
            </span>{" "}
            returns 410 Gone — do NOT use it.
          </p>

          <div className="grid grid-cols-2 gap-3 mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)]">Signing secret</div>
              <div data-testid="webhook-health-secret-last4" className="text-xs">
                {secretConfigured
                  ? <>Configured — last 4: <span className="text-[var(--ds-color-ink)]">{d.webhook_secret_last4}</span> ({d.webhook_secret_source})</>
                  : <span style={{ color: "var(--ds-color-danger)" }}>Not configured</span>}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)]">Last 24h</div>
              <div className="text-xs">
                <span data-testid="webhook-health-processed-count">{processed}</span> processed ·{" "}
                <span data-testid="webhook-health-failure-count" style={{ color: failures > 0 ? "var(--ds-color-danger)" : undefined }}>{failures}</span> signature failures
              </div>
            </div>
          </div>

          {d?.last_processed_event_at ? (
            <div className="text-[10px] text-[var(--ds-color-ink-muted)] mb-1">
              Last processed event: {d.last_processed_event_type} · {d.last_processed_event_at}
            </div>
          ) : null}
          {d?.last_paid_checkout_at ? (
            <div className="text-[10px] text-[var(--ds-color-ink-muted)] mb-2">
              Last paid checkout: {d.last_paid_checkout_at}
            </div>
          ) : null}

          <p data-testid="webhook-health-hint" className="text-[11px] leading-relaxed" style={{ color: statusLabel === "Healthy" ? "var(--ds-color-success)" : "var(--ds-color-warning)" }}>
            {d?.hint}
          </p>
        </>
      )}
    </div>
  );
}


/* Phase Stripe-1 P0 — Stripe key input row with:
 *   • "Currently saved" masked preview (last4 + source + hint)
 *   • Client-side prefix warning as you type
 *   • Explicit "Clear key" button (no more empty-string convention guessing)
 *   • On save, the parent skips fields that weren't edited this session so
 *     the encrypted store can never be overwritten by a masked placeholder.
 */
function StripeKeyField({
  testid,           // e.g. "cfg-stripe-secret"
  label,            // "Stripe Secret Key"
  isSet,            // boolean — true if a value is already saved
  last4,            // string or null — last-4 preview
  savedSource,      // "system_config" | "env" | null
  expectedPrefixes, // e.g. ["sk_test_", "sk_live_"]
  fieldName,        // e.g. "stripe_secret_key" — what the parent stores/sends
  value,            // controlled input value
  onChange,         // (newVal) => void
  onClear,          // () => void — parent flips a `to_clear` flag
  cleared,          // bool — parent's "user pressed Clear" flag
  hint,             // help text under the field
  isPassword = true,
}) {
  const [warning, setWarning] = React.useState(null);
  React.useEffect(() => {
    if (!value) { setWarning(null); return; }
    const stripped = value.trim();
    if (stripped === "") { setWarning(null); return; }
    // Client-side mirror of the backend validator so the admin sees the
    // problem before hitting Save.
    if (/[•●*…]/.test(stripped)) {
      setWarning("Looks like a masked preview — paste the FULL key from Stripe.");
      return;
    }
    if (!expectedPrefixes.some(p => stripped.startsWith(p))) {
      const wrongPrefix = stripped.slice(0, 8);
      setWarning(
        `Expected ${expectedPrefixes.join(" or ")} — got '${wrongPrefix}…'. ` +
        `sk_* = Secret key · pk_* = Publishable key · whsec_* = Webhook secret.`
      );
      return;
    }
    const longest = expectedPrefixes.reduce((a, b) => (b.length > a.length && stripped.startsWith(b) ? b : a), expectedPrefixes[0]);
    if (stripped.length < longest.length + 10) {
      setWarning(`Looks truncated — real Stripe keys are much longer than '${stripped}'.`);
      return;
    }
    setWarning(null);
  }, [value, expectedPrefixes]);

  return (
    <div className="mb-4" data-testid={`${testid}-row`}>
      <label className="block text-[11px] uppercase tracking-[0.15em] text-[var(--ds-color-ink-muted)] mb-1">
        {label}
      </label>
      {/* Currently-saved masked preview (visible when a value is set + hasn't been queued for clear this session) */}
      {isSet && !cleared && (
        <div
          data-testid={`${testid}-current`}
          className="mb-2 rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] px-3 py-2 flex items-center justify-between gap-3"
          style={{ background: "var(--ds-color-surface-muted)" }}
        >
          <div className="text-[11px] text-[var(--ds-color-ink-muted)] font-mono">
            Currently saved: <span className="text-[var(--ds-color-ink)]">
              {last4 ? `••••••••${last4}` : "••••••••"}
            </span>
            {savedSource && (
              <span className="ml-2 text-[10px] uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
                · source: {savedSource}
              </span>
            )}
          </div>
          <button
            data-testid={`${testid}-clear`}
            type="button"
            onClick={onClear}
            className="text-[10px] uppercase tracking-[0.14em] text-[var(--ds-color-danger)] hover:text-[var(--ds-color-danger)]/80 border border-[var(--ds-color-danger)] rounded px-2 py-0.5"
            title="Explicitly clear this key. Requires save to persist."
          >
            Clear key
          </button>
        </div>
      )}
      {cleared && (
        <div
          data-testid={`${testid}-cleared-pending`}
          className="mb-2 rounded-[var(--ds-radius-field)] border border-[var(--ds-color-danger)] px-3 py-2 text-[11px]"
          style={{ background: "var(--ds-color-danger-soft)", color: "var(--ds-color-danger)" }}
        >
          Will clear this key on next save. <button
            data-testid={`${testid}-undo-clear`}
            type="button"
            onClick={() => onClear(false)}
            className="ml-2 underline"
          >Undo</button>
        </div>
      )}
      <Input
        data-testid={testid}
        type={isPassword ? "password" : "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          cleared ? "Type a new value (or hit Save to clear)" :
          isSet ? "Leave blank to keep current" :
          `${expectedPrefixes[0]}…`
        }
        style={warning ? { borderColor: "var(--ds-color-danger)" } : undefined}
      />
      {warning ? (
        <p
          data-testid={`${testid}-warning`}
          className="text-[11px] text-[var(--ds-color-danger)] mt-1 leading-relaxed"
        >
          {warning}
        </p>
      ) : hint ? (
        <p className="text-[11px] text-[var(--ds-color-ink-muted)] mt-1 leading-relaxed">{hint}</p>
      ) : null}
    </div>
  );
}

export default function AdminSystemConfig() {
  const { isOwner } = useAdminPermissions();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [secretInput, setSecretInput] = useState({ stripe_secret_key: "", stripe_webhook_secret: "", postmark_server_token: "", lob_api_key: "" });
  // Phase Stripe-1 P0 — track explicit "Clear this key" intent per field.
  // Only sent to backend on save if the user pressed the Clear button.
  const [clearFlags, setClearFlags] = useState({
    stripe_secret_key: false,
    stripe_webhook_secret: false,
    stripe_publishable_key: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/system-config");
      setCfg(data);
      setSecretInput({ stripe_secret_key: "", stripe_webhook_secret: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  function set(field, value) {
    setCfg(c => ({ ...c, [field]: value }));
  }
  // Phase Stripe-1 P0 — track publishable-key edits distinctly so we
  // don't over-send the field on unrelated saves (which could re-persist
  // stale data or fail the new prefix validator).
  function setPublishableKey(newVal) {
    setCfg(c => ({ ...c, stripe_publishable_key: newVal, stripe_publishable_key_edited: true }));
    setClearFlags(f => ({ ...f, stripe_publishable_key: false }));
  }
  function toggleClear(field, value = true) {
    setClearFlags(f => ({ ...f, [field]: value }));
    if (field === "stripe_publishable_key" && value) {
      setCfg(c => ({ ...c, stripe_publishable_key: "", stripe_publishable_key_edited: false }));
    }
  }

  async function save() {
    setSaving(true);
    try {
      // Phase Stripe-1 P0 — Only include Stripe fields that the admin has
      // actually edited this session OR explicitly queued for clear. This
      // prevents a masked placeholder from ever hitting the backend and
      // overwriting a real key. Empty string is the explicit clear signal.
      const body = {
        business_name: cfg.business_name,
        business_legal_name: cfg.business_legal_name,
        business_logo_url: cfg.business_logo_url,
        primary_accent_color: cfg.primary_accent_color,
        support_email: cfg.support_email,
        billing_email: cfg.billing_email,
        notifications_from_email: cfg.notifications_from_email,
        company_address: cfg.company_address,
        company_phone: cfg.company_phone,
        company_website: cfg.company_website,
        stripe_mode: cfg.stripe_mode,
        default_currency: cfg.default_currency,
        environment_label: cfg.environment_label,
        maintenance_mode: cfg.maintenance_mode,
      };
      // Publishable key — send only when user typed a new value OR explicitly
      // pressed Clear. Otherwise leave the DB row alone.
      if (clearFlags.stripe_publishable_key) {
        body.stripe_publishable_key = "";
      } else if (cfg.stripe_publishable_key_edited) {
        body.stripe_publishable_key = cfg.stripe_publishable_key;
      }
      // Secret key
      if (clearFlags.stripe_secret_key) {
        body.stripe_secret_key = "";
      } else if (secretInput.stripe_secret_key) {
        body.stripe_secret_key = secretInput.stripe_secret_key;
      }
      // Webhook secret
      if (clearFlags.stripe_webhook_secret) {
        body.stripe_webhook_secret = "";
      } else if (secretInput.stripe_webhook_secret) {
        body.stripe_webhook_secret = secretInput.stripe_webhook_secret;
      }
      if (secretInput.postmark_server_token) body.postmark_server_token = secretInput.postmark_server_token;
      if (secretInput.lob_api_key) body.lob_api_key = secretInput.lob_api_key;
      body.lob_publishable_key = cfg.lob_publishable_key || "";
      body.credit_hero_referral_url = cfg.credit_hero_referral_url || "";
      body.credit_hero_partner_id = cfg.credit_hero_partner_id || "";
      body.credit_hero_enabled = !!cfg.credit_hero_enabled;
      body.postmark_from_email = cfg.postmark_from_email || "";
      // eslint-disable-next-line no-console
      console.info("[system-config] Save → PATCH body keys:", Object.keys(body).sort());
      const { data } = await api.patch("/admin/system-config", body);
      setCfg(data);
      setSecretInput({ stripe_secret_key: "", stripe_webhook_secret: "", postmark_server_token: "", lob_api_key: "" });
      setClearFlags({ stripe_secret_key: false, stripe_webhook_secret: false, stripe_publishable_key: false });
      // Success message calls out which Stripe fields landed so admin can't miss it.
      const changed = [];
      if (body.stripe_secret_key !== undefined) changed.push(`Secret key (${data.stripe_secret_key_set ? `set …${data.stripe_secret_key_last4 || "—"}` : "cleared"})`);
      if (body.stripe_webhook_secret !== undefined) changed.push(`Webhook secret (${data.stripe_webhook_secret_set ? "set" : "cleared"})`);
      if (body.stripe_publishable_key !== undefined) changed.push(`Publishable key (${data.stripe_publishable_key ? `set '${data.stripe_publishable_key.slice(0,8)}…'` : "cleared"})`);
      if (changed.length) {
        toast.success(`Saved · ${changed.join(" · ")}`);
      } else {
        toast.success("System config saved");
      }
      // Force-refresh the Stripe reachability probe so the mode badge below
      // reflects the newly-saved key without a 5-min stale window.
      try {
        await api.post("/admin/diagnostic/reprobe-stripe");
      } catch (probeErr) {
        // Non-fatal — the save itself succeeded; just log.
        // eslint-disable-next-line no-console
        console.warn("[system-config] Re-probe after save failed:", probeErr);
      }
    } catch (e) {
      // Surface per-field 422 errors with a clear message so the admin
      // knows WHICH field is wrong AND why.
      // eslint-disable-next-line no-console
      console.error("[system-config] Save failed:", e?.response?.status, e?.response?.data);
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail) && detail.length) {
        detail.slice(0, 3).forEach((err) => {
          const loc = Array.isArray(err.loc) ? err.loc[err.loc.length - 1] : "field";
          const msg = err.msg?.replace(/^Value error, ?/, "") || "invalid value";
          toast.error(`${loc}: ${msg}`);
        });
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSaving(false);
    }
  }

  async function testStripe() {
    try {
      const { data } = await api.post("/admin/system-config/test-stripe");
      if (data?.ok) {
        // Phase Stripe-1 P0 — new response shape includes account_id + livemode.
        const lm = data.livemode ? "LIVE" : "TEST";
        toast.success(
          `Stripe ${data.mode || "?"}: authenticated as ${data.account_id || "?"} (${lm} key, source=${data.source || "?"})`
        );
      } else {
        const code = data?.error || "unknown_error";
        const detail = data?.message ? ` — ${data.message}` : "";
        toast.error(`Stripe test failed: ${code}${detail}`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function testPostmark() {
    try {
      const { data } = await api.post("/admin/system-config/test-postmark");
      toast.message(`Postmark ${data.mode}: ${data.message}`);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function testLob() {
    try {
      const { data } = await api.post("/admin/system-config/test-lob");
      if (data?.ok) toast.success(`Lob ${data.mode}: ${data.message || "OK"}`);
      else toast.error(`Lob test failed: ${data?.error || "unknown error"}`);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  // V1.1e — Credit Hero connection test
  const [chTesting, setChTesting] = useState(false);
  const [chLastResult, setChLastResult] = useState(null);
  async function testCreditHero() {
    setChTesting(true);
    try {
      const { data } = await api.post("/admin/system-config/test-credit-hero");
      setChLastResult(data);
      if (data?.ok) toast.success(`Credit Hero: ${data.message} (${data.response_time_ms}ms)`);
      else toast.error(`Credit Hero: ${data.message}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setChTesting(false); }
  }

  function statusPillStyles(status) {
    if (status === "ok")               return { color: "var(--ds-color-success)", bg: "var(--ds-color-success-soft)", border: "var(--ds-color-success-soft)", label: "OK" };
    if (status === "url_unreachable")  return { color: "var(--ds-color-danger)",  bg: "var(--ds-color-danger-soft)",  border: "var(--ds-color-danger-soft)",  label: "URL unreachable" };
    if (status === "redirect_invalid") return { color: "var(--ds-color-danger)",  bg: "var(--ds-color-danger-soft)",  border: "var(--ds-color-danger-soft)",  label: "Invalid URL" };
    return                                    { color: "var(--ds-color-ink-muted)", bg: "var(--ds-color-surface-muted)", border: "var(--ds-color-border)",       label: "Untested" };
  }

  if (!isOwner) {
    return (
      <AdminLayout title="System Config" subtitle="Workspace-wide branding, billing, and environment">
        <div
          data-testid="system-config-denied"
          className="rounded-[var(--ds-radius-card)] p-8 text-center"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-admin-border)",
          }}
        >
          <Lock className="w-6 h-6 mx-auto mb-2 text-[var(--ds-color-ink-muted)]" />
          <p className="text-sm text-[var(--ds-color-ink-muted)]">Owner-only. Ask the workspace owner to edit system configuration.</p>
        </div>
      </AdminLayout>
    );
  }

  if (loading || !cfg) {
    return <AdminLayout title="System Config"><div data-testid="system-config-loading" className="text-sm text-[var(--ds-color-ink-muted)]">Loading…</div></AdminLayout>;
  }

  return (
    <AdminLayout
      title="System Config"
      subtitle="Workspace-wide branding, billing, and environment"
      actions={
        <button data-testid="system-config-save" disabled={saving} onClick={save} className="inline-flex items-center gap-1.5 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold disabled:opacity-60 transition-colors" style={{ background: "var(--ds-color-accent)", color: "var(--ds-color-accent-ink)", transitionDuration: "var(--ds-duration-fast)" }} onMouseEnter={(e) => !saving && (e.currentTarget.style.background = "var(--ds-color-accent-strong)")} onMouseLeave={(e) => (e.currentTarget.style.background = "var(--ds-color-accent)")}>
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
        </button>
      }
    >
      <AdminPageHelp
        title="System Config"
        purpose="Workspace-level branding, billing anchor points, referral partners, PDF letterhead, and admin-invitation defaults. This is the owner-only 'company preferences' surface."
        configures="Business name / legal name / logo URL / primary accent color; support & billing contact emails; billing address + Stripe customer link; the 3 referral partner tiles (Credit Hero, business formation, business bank); PDF letterhead; and admin invitation defaults."
        impact="User-visible surfaces (footer, letterhead, contact links, referral tiles) update on next page load. Stripe customer changes are one-way — once linked, the workspace bills against that customer."
        safety="Yes for branding / referrals / contact copy — paste the previous value back to revert. Stripe linkage should not be swapped in a live workspace; contact support before touching it."
      />
      <Section title="Branding" icon={Building2} testid="section-branding">
        <Field label="Business name"><Input data-testid="cfg-business-name" value={cfg.business_name || ""} onChange={(e) => set("business_name", e.target.value)} /></Field>
        <Field label="Legal name (optional)"><Input data-testid="cfg-legal-name" value={cfg.business_legal_name || ""} onChange={(e) => set("business_legal_name", e.target.value)} /></Field>
        <Field label="Logo URL"><Input data-testid="cfg-logo-url" value={cfg.business_logo_url || ""} onChange={(e) => set("business_logo_url", e.target.value)} placeholder="https://..." /></Field>
        <Field label="Primary accent (hex)"><Input data-testid="cfg-accent" value={cfg.primary_accent_color || ""} onChange={(e) => set("primary_accent_color", e.target.value)} placeholder="Six-character hex, e.g. #______" /></Field>
      </Section>

      <Section title="Contact emails" icon={Mail} testid="section-contact">
        <Field label="Support email"><Input data-testid="cfg-support-email" type="email" value={cfg.support_email || ""} onChange={(e) => set("support_email", e.target.value)} /></Field>
        <Field label="Billing email"><Input data-testid="cfg-billing-email" type="email" value={cfg.billing_email || ""} onChange={(e) => set("billing_email", e.target.value)} /></Field>
        <Field label="Notifications from address" hint="Defaults to the Postmark sender — overridden in 8b.2."><Input data-testid="cfg-notif-email" type="email" value={cfg.notifications_from_email || ""} onChange={(e) => set("notifications_from_email", e.target.value)} /></Field>
      </Section>

      <Section title="Company info" icon={Globe2} testid="section-company">
        <Field label="Address"><Input data-testid="cfg-address" value={cfg.company_address || ""} onChange={(e) => set("company_address", e.target.value)} /></Field>
        <Field label="Phone"><Input data-testid="cfg-phone" value={cfg.company_phone || ""} onChange={(e) => set("company_phone", e.target.value)} /></Field>
        <Field label="Website"><Input data-testid="cfg-website" value={cfg.company_website || ""} onChange={(e) => set("company_website", e.target.value)} placeholder="https://..." /></Field>
      </Section>

      <Section title="Billing / Stripe" icon={CreditCard} testid="section-billing"
        badge={
          <div className="flex items-center gap-2">
            <StripeModeBadge />
            <button data-testid="cfg-test-stripe" onClick={testStripe} className="rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] px-3 py-1.5 text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"><ShieldCheck className="w-3 h-3 inline mr-1" /> Test connection</button>
          </div>
        }
      >
        <p className="text-[11px] text-[var(--ds-color-ink-muted)] leading-relaxed mb-2">
          Mode is auto-detected from the secret key prefix: <span className="font-mono">sk_test_</span> → test,
          <span className="font-mono"> sk_live_</span> → live, otherwise stub. The legacy admin-set Mode dropdown
          below is kept for downstream code that hasn't migrated yet — the diagnostic + checkout endpoints
          use the auto-detected mode regardless of this value.
        </p>
        <Field label="Legacy mode (cosmetic)">
          <select data-testid="cfg-stripe-mode" value={cfg.stripe_mode || "stub"} onChange={(e) => set("stripe_mode", e.target.value)} className="w-full rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] bg-transparent px-3 py-2 text-sm text-[var(--ds-color-ink)]" style={{ background: "var(--ds-color-surface)" }}>
            <option value="stub">Stub (no live calls)</option>
            <option value="test">Test (Stripe test cards)</option>
            <option value="live">Live (real charges)</option>
          </select>
        </Field>
        <StripeKeyField
          testid="cfg-stripe-pub"
          label="Publishable key"
          isSet={!!cfg.stripe_publishable_key}
          last4={cfg.stripe_publishable_key ? cfg.stripe_publishable_key.slice(-4) : null}
          savedSource="system_config"
          expectedPrefixes={["pk_test_", "pk_live_"]}
          fieldName="stripe_publishable_key"
          value={cfg.stripe_publishable_key || ""}
          onChange={(v) => setPublishableKey(v)}
          onClear={(v = true) => toggleClear("stripe_publishable_key", v)}
          cleared={clearFlags.stripe_publishable_key}
          isPassword={false}
          hint="Public key — safe to expose. Only sk_/whsec_ are stored encrypted."
        />
        <StripeKeyField
          testid="cfg-stripe-secret"
          label="Secret key"
          isSet={!!cfg.stripe_secret_key_set}
          last4={cfg.stripe_secret_key_last4}
          savedSource="system_config"
          expectedPrefixes={["sk_test_", "sk_live_"]}
          fieldName="stripe_secret_key"
          value={secretInput.stripe_secret_key}
          onChange={(v) => { setSecretInput(s => ({ ...s, stripe_secret_key: v })); setClearFlags(f => ({ ...f, stripe_secret_key: false })); }}
          onClear={(v = true) => toggleClear("stripe_secret_key", v)}
          cleared={clearFlags.stripe_secret_key}
          hint="Stored encrypted at rest (Fernet). Type a new value to replace, or press Clear key to remove."
        />
        <StripeKeyField
          testid="cfg-stripe-webhook-secret"
          label="Webhook signing secret"
          isSet={!!cfg.stripe_webhook_secret_set}
          last4={null}
          savedSource="system_config"
          expectedPrefixes={["whsec_"]}
          fieldName="stripe_webhook_secret"
          value={secretInput.stripe_webhook_secret}
          onChange={(v) => { setSecretInput(s => ({ ...s, stripe_webhook_secret: v })); setClearFlags(f => ({ ...f, stripe_webhook_secret: false })); }}
          onClear={(v = true) => toggleClear("stripe_webhook_secret", v)}
          cleared={clearFlags.stripe_webhook_secret}
          hint="From Stripe → Developers → Webhooks → Signing secret. Required for /api/webhook/stripe verification."
        />
        <WebhookHealthCard />
        <Field label="Default currency"><Input data-testid="cfg-currency" value={cfg.default_currency || "usd"} onChange={(e) => set("default_currency", e.target.value)} /></Field>
      </Section>

      <Section title="Postmark" icon={Mail} testid="section-postmark"
        badge={<span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded" style={{
          color: cfg.postmark_effective_mode === "live" ? "var(--ds-color-success)" : "var(--ds-color-warning)",
          background: cfg.postmark_effective_mode === "live" ? "var(--ds-color-success-soft)" : "var(--ds-color-warning-soft)",
        }}>{cfg.postmark_effective_mode === "live" ? "Live" : "Sandbox"}</span>}
      >
        <p className="text-xs text-[var(--ds-color-ink-muted)]">In sandbox mode, emails are validated but not delivered. Add a real Postmark Server Token and verified Sender Signature to enable real sends.</p>
        <Field label={`Server Token (${cfg.postmark_server_token_set ? `set, ending …${cfg.postmark_server_token_last4 || "—"}` : "not set"})`} hint="Find it in Postmark → Servers → API tokens. Stored encrypted at rest.">
          <Input data-testid="cfg-postmark-token" type="password" value={secretInput.postmark_server_token || ""} onChange={(e) => setSecretInput(s => ({ ...s, postmark_server_token: e.target.value }))} placeholder={cfg.postmark_server_token_set ? "Leave blank to keep" : "Server token"} />
        </Field>
        <Field label="Sender Signature (verified From email)" hint="Use a verified Sender Signature in Postmark before going live."><Input data-testid="cfg-postmark-from" type="email" value={cfg.postmark_from_email || ""} onChange={(e) => set("postmark_from_email", e.target.value)} placeholder="noreply@example.com" /></Field>
        <PostmarkWebhookRow cfg={cfg} reload={load} secretInput={secretInput} setSecretInput={setSecretInput} />
        <Field label="Test current Postmark connection">
          <button data-testid="cfg-test-postmark" onClick={testPostmark} className="rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] px-3 py-1.5 text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"><ShieldCheck className="w-3 h-3 inline mr-1" /> Test</button>
        </Field>
      </Section>

      <Section title="Letter mailing (Lob)" icon={Send} testid="section-lob"
        badge={<span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded" style={{
          color: cfg.lob_effective_mode === "live" ? "var(--ds-color-success)" : "var(--ds-color-warning)",
          background: cfg.lob_effective_mode === "live" ? "var(--ds-color-success-soft)" : "var(--ds-color-warning-soft)",
        }}>{cfg.lob_effective_mode === "live" ? "Live" : "Sandbox"}</span>}
      >
        <p className="text-xs text-[var(--ds-color-ink-muted)]">
          Lob handles printing &amp; USPS handoff for dispute letters. In sandbox mode, letters never leave your environment — we simulate the timeline over ~90 seconds. Add a Lob Live API key to enable real mailing.
        </p>
        <Field label="Publishable key (optional, used for future client-side address autocomplete)">
          <Input data-testid="cfg-lob-pub" value={cfg.lob_publishable_key || ""} onChange={(e) => set("lob_publishable_key", e.target.value)} placeholder="live_pub_… or test_pub_…" />
        </Field>
        <Field
          label={`API key (${cfg.lob_api_key_set ? `set, ending …${cfg.lob_api_key_last4 || "—"}` : "not set"})`}
          hint="Find it in Lob → Settings → API Keys. Stored encrypted at rest. Leave blank to keep current; submit empty string to clear."
        >
          <Input
            data-testid="cfg-lob-secret"
            type="password"
            value={secretInput.lob_api_key}
            onChange={(e) => setSecretInput((s) => ({ ...s, lob_api_key: e.target.value }))}
            placeholder={cfg.lob_api_key_set ? "Leave blank to keep" : "live_… or test_…"}
          />
        </Field>
        <LobWebhookRow cfg={cfg} reload={load} />
        <Field label="Test current Lob connection">
          <button data-testid="cfg-test-lob" onClick={testLob} className="rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] px-3 py-1.5 text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]">
            <ShieldCheck className="w-3 h-3 inline mr-1" /> Test
          </button>
        </Field>
      </Section>

      <Section title="Credit Reports (Credit Hero)" icon={Link2} testid="section-credit-hero"
        badge={<span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded" style={{
          color: "var(--ds-color-warning)",
          background: "var(--ds-color-warning-soft)",
        }}>Affiliate Redirect</span>}
      >
        <p className="text-xs text-[var(--ds-color-ink-muted)] leading-relaxed">
          Currently users are redirected to Credit Hero to pull their 3-bureau credit reports. Configure the affiliate URL below. Full Credit Hero API integration is not available — for direct credit report pulls, see Array.com / IDIQ in the V1.2 roadmap.
        </p>
        <label className="flex items-center gap-2 text-sm text-[var(--ds-color-ink-muted)] pt-1">
          <input
            data-testid="cfg-credit-hero-enabled"
            type="checkbox"
            checked={!!cfg.credit_hero_enabled}
            onChange={(e) => set("credit_hero_enabled", e.target.checked)}
          />
          Show "Pull My 3-Bureau Credit Report" button to users
        </label>
        <Field label="Affiliate referral URL" hint="Users are redirected here from /credit-report.">
          <div className="flex gap-2">
            <Input
              data-testid="cfg-credit-hero-url"
              type="url"
              value={cfg.credit_hero_referral_url || ""}
              onChange={(e) => set("credit_hero_referral_url", e.target.value)}
              placeholder="https://signup.credithero.com/?referrer=..."
            />
            {cfg.credit_hero_referral_url && (
              <a
                href={cfg.credit_hero_referral_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cfg-credit-hero-visit"
                className="inline-flex items-center px-3 py-2 rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] whitespace-nowrap"
              >
                Visit URL
              </a>
            )}
          </div>
        </Field>
        <Field label="Partner ID (optional)" hint="Admin label only — e.g. ACC-12345. Stored alongside the URL for your records.">
          <Input
            data-testid="cfg-credit-hero-partner-id"
            value={cfg.credit_hero_partner_id || ""}
            onChange={(e) => set("credit_hero_partner_id", e.target.value)}
            placeholder="ACC-12345"
          />
        </Field>
        <div
          data-testid="cfg-credit-hero-status-panel"
          className="rounded-[var(--ds-radius-card)] border border-[var(--ds-color-admin-border)] p-3 mt-2 flex items-center gap-3 flex-wrap"
          style={{ background: "var(--ds-color-surface-muted)" }}
        >
          <span
            data-testid="cfg-credit-hero-status-pill"
            className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full"
            style={(() => { const s = statusPillStyles(cfg.credit_hero_last_health_status); return { color: s.color, background: s.bg, border: `1px solid ${s.border}` }; })()}
          >
            {statusPillStyles(cfg.credit_hero_last_health_status).label}
          </span>
          <div className="text-[11px] text-[var(--ds-color-ink-muted)] flex-1 min-w-0">
            {cfg.credit_hero_last_health_check
              ? <>Last tested {new Date(cfg.credit_hero_last_health_check).toLocaleString()}{chLastResult?.response_time_ms != null && ` · ${chLastResult.response_time_ms}ms`}{chLastResult?.status_code != null && ` · HTTP ${chLastResult.status_code}`}</>
              : "Never tested"}
          </div>
          <button
            data-testid="cfg-test-credit-hero"
            onClick={testCreditHero}
            disabled={chTesting}
            className="rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] px-3 py-1.5 text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] disabled:opacity-50"
          >
            <ShieldCheck className="w-3 h-3 inline mr-1" /> {chTesting ? "Testing…" : "Test connection"}
          </button>
        </div>
      </Section>

      <Section title="Environment" icon={AlertTriangle} testid="section-environment">
        <Field label="Environment label" hint="When set to 'sandbox', a sticky banner appears across all admin pages.">
          <select data-testid="cfg-env-label" value={cfg.environment_label || "sandbox"} onChange={(e) => set("environment_label", e.target.value)} className="w-full rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] bg-transparent px-3 py-2 text-sm text-[var(--ds-color-ink)]" style={{ background: "var(--ds-color-surface)" }}>
            <option value="sandbox">Sandbox</option>
            <option value="production">Production</option>
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm text-[var(--ds-color-ink-muted)]">
          <input data-testid="cfg-maintenance" type="checkbox" checked={!!cfg.maintenance_mode} onChange={(e) => set("maintenance_mode", e.target.checked)} />
          Maintenance mode (display a banner — full enforcement ships later)
        </label>
        <div className="text-[11px] text-[var(--ds-color-ink-muted)]">Last updated: {cfg.updated_at ? new Date(cfg.updated_at).toLocaleString() : "—"} {cfg.updated_by_email ? `by ${cfg.updated_by_email}` : ""}</div>
      </Section>
      <ProductionReadinessPanel />
    </AdminLayout>
  );
}


/* V1.1-Beta — Production Readiness Check panel.
 * Hits the unauthenticated `/api/_diagnostic` endpoint and renders the
 * integration modes + DB connectivity. Gives ops a one-glance check.
 *
 * V1.2 — LLM row is now driven by a live cached probe (see backend
 * `modules/diagnostic/llm_probe.py`). Surfaces probe timestamp and a
 * "Re-probe now" affordance that bypasses the 5-minute cache via the
 * admin-gated `POST /api/admin/diagnostic/reprobe-llm` endpoint.
 */
function ProductionReadinessPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reprobing, setReprobing] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data: d } = await api.get("/_diagnostic");
      setData(d);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function reprobeLlm() {
    setReprobing(true);
    try {
      const { data: probe } = await api.post("/admin/diagnostic/reprobe-llm");
      // Merge the fresh probe state into our local diagnostic snapshot so the
      // panel reflects the new status without waiting on /api/_diagnostic.
      setData((prev) => prev ? {
        ...prev,
        integrations: { ...(prev.integrations || {}), llm: probe.status },
        llm_probed_at: probe.probed_at,
        llm_probe_note: probe.note,
      } : prev);
      if (probe.status === "live")          toast.success("LLM probe: live — budget verified.");
      else if (probe.status === "depleted") toast.error("LLM probe: budget depleted. Top up at Profile → Universal Key.");
      else if (probe.status === "key_missing") toast.error("LLM probe: key missing or invalid.");
      else                                  toast.error(`LLM probe failed: ${probe.note || "unknown error"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReprobing(false);
    }
  }

  const readyForProduction = !!(data && data.db === "connected" && data.env_mode === "production");
  const llmStatus = data?.integrations?.llm;
  const llmIsLive = llmStatus === "live";
  // Show the Re-probe button always (admins want to re-verify on demand). Use
  // alarm colours when the status is anything other than "live" so a depleted
  // budget visually jumps out.
  const llmStatusColor =
    llmStatus === "live"        ? { fg: "var(--ds-color-success)", bg: "var(--ds-color-success-soft)" } :
    llmStatus === "depleted"    ? { fg: "var(--ds-color-danger)", bg: "var(--ds-color-danger-soft)" } :
    llmStatus === "key_missing" ? { fg: "var(--ds-color-danger)", bg: "var(--ds-color-danger-soft)" } :
    llmStatus === "error"       ? { fg: "var(--ds-color-warning)", bg: "var(--ds-color-warning-soft)" } :
                                  { fg: "var(--ds-color-ink-muted)", bg: "var(--ds-color-border)" };

  return (
    <Section title="Production Readiness" icon={Server} testid="section-production-readiness">
      <div className="text-xs text-[var(--ds-color-ink-muted)] mb-3">
        Live snapshot from <span className="font-mono">/api/_diagnostic</span>. Shareable; no auth required.
      </div>
      {loading || !data ? (
        <div className="text-sm text-[var(--ds-color-ink-muted)]">Loading…</div>
      ) : (
        <div className="space-y-2" data-testid="production-readiness">
          <ReadinessRow label="Database" value={data.db} ok={data.db === "connected"} testId="readiness-db" />
          <ReadinessRow label="Environment" value={data.env_mode} ok={data.env_mode === "production"} testId="readiness-env" />
          <ReadinessRow label="Stripe" value={data.integrations?.stripe} ok={data.integrations?.stripe === "live" || data.integrations?.stripe === "test"} testId="readiness-stripe" />
          <ReadinessRow label="Postmark" value={data.integrations?.postmark} ok={data.integrations?.postmark === "live"} testId="readiness-postmark" />
          <ReadinessRow label="Lob" value={data.integrations?.lob} ok={data.integrations?.lob === "live"} testId="readiness-lob" />

          {/* V1.2 — LLM probe sub-panel (status + probed_at + Re-probe). */}
          <div
            data-testid="readiness-llm"
            className="rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] p-3 mt-1"
            style={{ background: "var(--ds-color-surface-muted)" }}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <Wand2 className="w-3.5 h-3.5 text-[var(--ds-color-accent)] shrink-0" />
                <span className="text-sm text-[var(--ds-color-ink-muted)]">LLM (Universal Key)</span>
                <span
                  title={data.integration_notes?.llm}
                  className="cursor-help text-[10px] text-[var(--ds-color-ink-muted)] border border-[var(--ds-color-admin-border)] rounded-full w-3.5 h-3.5 inline-flex items-center justify-center"
                >?</span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  data-testid="readiness-llm-status"
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5"
                  style={{ background: llmStatusColor.bg, color: llmStatusColor.fg }}
                >
                  {llmStatus || "unknown"}
                </span>
                <button
                  data-testid="readiness-llm-reprobe"
                  onClick={reprobeLlm}
                  disabled={reprobing}
                  className="inline-flex items-center gap-1.5 rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] px-2.5 py-1 text-[11px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] disabled:opacity-50"
                >
                  <RefreshCcw className={`w-3 h-3 ${reprobing ? "animate-spin" : ""}`} />
                  {reprobing ? "Re-probing…" : "Re-probe now"}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between text-[11px] text-[var(--ds-color-ink-muted)] mt-2 gap-3 flex-wrap">
              <span data-testid="readiness-llm-probed-at">
                {data.llm_probed_at
                  ? <>Last probed {new Date(data.llm_probed_at).toLocaleString()}</>
                  : "Never probed"}
              </span>
              {data.llm_probe_note && (
                <span
                  data-testid="readiness-llm-note"
                  className="font-mono truncate max-w-[60%]"
                  title={data.llm_probe_note}
                >
                  {data.llm_probe_note}
                </span>
              )}
            </div>
            {!llmIsLive && (
              <p
                data-testid="readiness-llm-warning"
                className="text-[11px] mt-2 leading-relaxed"
                style={{ color: llmStatusColor.fg }}
              >
                {llmStatus === "depleted" && "Universal LLM Key budget is exhausted. Top up at Profile → Universal Key → Add Balance, then re-probe."}
                {llmStatus === "key_missing" && "EMERGENT_LLM_KEY env var is not configured (or auth failed). Set the key and re-probe."}
                {llmStatus === "error" && "Probe failed. Check backend logs and re-probe. See note above."}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between text-xs pt-2 mt-2 border-t border-[var(--ds-color-admin-border)]">
            <span className="text-[var(--ds-color-ink-muted)]">Uptime</span>
            <span className="text-[var(--ds-color-ink-muted)]">{Math.floor((data.uptime_seconds || 0) / 60)}m</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-[var(--ds-color-ink-muted)]">Git SHA</span>
            <span className="font-mono text-[var(--ds-color-ink-muted)]">{(data.git_sha || "").slice(0, 12)}</span>
          </div>
          <div
            data-testid="readiness-overall"
            className="mt-3 rounded-[var(--ds-radius-field)] px-3 py-2 text-xs inline-flex items-center gap-2"
            style={{
              background: readyForProduction ? "var(--ds-color-success-soft)" : "var(--ds-color-warning-soft)",
              color: readyForProduction ? "var(--ds-color-success)" : "var(--ds-color-warning)",
            }}
          >
            {readyForProduction ? "Ready for production" : "Not yet production-ready"}
          </div>
        </div>
      )}
    </Section>
  );
}

function ReadinessRow({ label, value, ok, testId, help }) {
  return (
    <div className="flex items-center justify-between text-sm" data-testid={testId}>
      <span className="text-[var(--ds-color-ink-muted)] inline-flex items-center gap-1.5">
        {label}
        {help && (
          <span
            title={help}
            className="cursor-help text-[10px] text-[var(--ds-color-ink-muted)] border border-[var(--ds-color-admin-border)] rounded-full w-3.5 h-3.5 inline-flex items-center justify-center"
          >
            ?
          </span>
        )}
      </span>
      <span
        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5"
        style={{
          background: ok ? "var(--ds-color-success-soft)" : "var(--ds-color-warning-soft)",
          color: ok ? "var(--ds-color-success)" : "var(--ds-color-warning)",
        }}
      >
        {value || "—"}
      </span>
    </div>
  );
}

/* Phase Stripe-1 — auto-detected Stripe mode badge + re-probe button.
 * Sits next to the "Test connection" badge in the Stripe section header.
 * The mode label comes from `/api/_diagnostic` (which uses the new key
 * resolver under modules/billing/key_resolver.py). The "Re-probe" button
 * calls POST /api/admin/diagnostic/reprobe-stripe so admins can refresh
 * the pill the instant they paste a new key + Save.
 */
function StripeModeBadge() {
  const [mode, setMode] = useState(null);
  const [source, setSource] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchMode = useCallback(async () => {
    try {
      const { data } = await api.get("/_diagnostic");
      setMode(data?.integrations?.stripe || "stub");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    fetchMode();
  }, [fetchMode]);

  async function reprobe() {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/diagnostic/reprobe-stripe");
      setMode(data?.status || "stub");
      setSource(data?.source || null);
      const label = data?.status || "stub";
      // eslint-disable-next-line no-console
      console.info("[system-config] Re-probe stripe →", data);
      if (label === "live")        toast.success(`Stripe live mode detected (sk_live_*). Source: ${data?.source || "?"}`);
      else if (label === "test")   toast.success(`Stripe test mode detected (sk_test_*). Source: ${data?.source || "?"}`);
      else if (label === "invalid_key") toast.error(
        `Stripe rejected the API key (invalid_key, last4=${data?.last4 || "?"}). ` +
        `Paste a fresh sk_test_ / sk_live_ from Stripe → Developers → API keys.`
      );
      else if (label === "unreachable") toast.error(`Cannot reach Stripe — ${data?.error || "network/timeout"}. Try again.`);
      else if (label === "stub") toast.error(
        `Stored key isn't the shape of a real Stripe secret (last4=${data?.last4 || "?"}). ` +
        `Common cause: pasting a whsec_* or pk_* value into the Secret Key field.`
      );
      else                          toast.error("No Stripe key resolved — checkout endpoints will 503 until a key is pasted under Billing → Secret key.");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[system-config] Re-probe stripe failed:", e?.response?.status, e?.response?.data);
      toast.error(`Stripe re-probe failed: ${formatApiError(e)}`);
    } finally {
      setBusy(false);
    }
  }

  const color =
    mode === "live" ? { bg: "var(--ds-color-success-soft)", fg: "var(--ds-color-success)", label: "Live" } :
    mode === "test" ? { bg: "var(--ds-color-accent-soft)", fg: "var(--ds-color-accent)", label: "Test" } :
                       { bg: "var(--ds-color-warning-soft)", fg: "var(--ds-color-warning)", label: "Stub" };

  return (
    <div className="flex items-center gap-1.5">
      <span
        data-testid="cfg-stripe-mode-detected"
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] rounded-full px-2 py-0.5"
        style={{ background: color.bg, color: color.fg }}
        title={source ? `Resolved from ${source}` : ""}
      >
        Mode: {color.label}
      </span>
      <button
        data-testid="cfg-stripe-reprobe"
        onClick={reprobe}
        disabled={busy}
        className="inline-flex items-center gap-1 rounded-[var(--ds-radius-field)] border border-[var(--ds-color-admin-border)] px-2 py-1 text-[11px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] disabled:opacity-50"
      >
        <RefreshCcw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
        {busy ? "Re-probing…" : "Re-probe"}
      </button>
    </div>
  );
}

